import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI, utils } from '../api/authAPI';
import { withAppBasename } from '../../../config/appBase';
import './Login.css';

/**
 * Forgot / reset password — same workay layout as Login.js.
 * No token → email request form. With ?token= → set new password.
 */
const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSuccess, setIsSuccess] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const validateResetForm = () => {
    const newErrors = {};
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    const trimmed = (email || '').trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }
    setIsLoading(true);
    setErrors({});
    try {
      await authAPI.forgotPassword(trimmed);
      setEmailSent(true);
    } catch (error) {
      setErrors({ submit: utils.formatErrorMessage(error) });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      setErrors({ submit: 'Missing token. Please request a new password reset link.' });
      return;
    }
    if (!validateResetForm()) return;
    setIsLoading(true);
    setErrors({});
    try {
      await authAPI.resetPassword(token, password);
      setIsSuccess(true);
    } catch (error) {
      setErrors({ submit: utils.formatErrorMessage(error) });
    } finally {
      setIsLoading(false);
    }
  };

  const brandHeader = (
    <div className="workay-brand-header">
      <a
        href={withAppBasename('/')}
        onClick={(e) => {
          e.preventDefault();
          window.location.href = withAppBasename('/');
        }}
        className="workay-logo-link"
      >
        <img src="/logo-yellow.png" alt="Pathbuddy Logo" className="workay-logo-img" />
        <span className="workay-brand-title">Pathbuddy</span>
      </a>
    </div>
  );

  const backToLogin = (
    <div className="workay-footer-toggle" style={{ marginTop: '20px' }}>
      <button
        type="button"
        onClick={() => navigate('/login')}
        className="workay-link-underline"
      >
        Back to Sign in
      </button>
    </div>
  );

  let body;
  if (isSuccess) {
    body = (
      <>
        {brandHeader}
        <div className="workay-headline-group">
          <h1 className="workay-title">Password updated</h1>
          <p className="workay-subtitle">You can now sign in with your new password.</p>
        </div>
        <button
          type="button"
          className="workay-submit-btn"
          onClick={() => navigate('/login')}
        >
          Back to Sign in
        </button>
      </>
    );
  } else if (emailSent) {
    body = (
      <>
        {brandHeader}
        <div className="workay-headline-group">
          <h1 className="workay-title">Check your email</h1>
          <p className="workay-subtitle">
            If that email is registered, we sent a reset link. Open it to choose a new password.
          </p>
        </div>
        {backToLogin}
      </>
    );
  } else if (!token) {
    body = (
      <>
        {brandHeader}
        <div className="workay-headline-group">
          <h1 className="workay-title">Forgot password</h1>
          <p className="workay-subtitle">Enter your email and we will send a reset link.</p>
        </div>
        {errors.submit && (
          <div className="workay-alert error">
            <span>{errors.submit}</span>
          </div>
        )}
        <form className="workay-form" onSubmit={handleForgotSubmit} noValidate>
          <div className="workay-field">
            <label className="workay-label">E-mail address</label>
            <input
              type="email"
              className="workay-input"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              autoComplete="email"
              required
            />
            {errors.email && <span className="workay-field-error">{errors.email}</span>}
          </div>
          <button type="submit" className="workay-submit-btn" disabled={isLoading}>
            {isLoading ? 'Sending...' : 'Send reset link'}
          </button>
        </form>
        {backToLogin}
      </>
    );
  } else {
    body = (
      <>
        {brandHeader}
        <div className="workay-headline-group">
          <h1 className="workay-title">Reset password</h1>
          <p className="workay-subtitle">Create a secure new password for your account.</p>
        </div>
        {errors.submit && (
          <div className="workay-alert error">
            <span>{errors.submit}</span>
          </div>
        )}
        <form className="workay-form" onSubmit={handleResetSubmit} noValidate>
          <div className="workay-field">
            <label className="workay-label">New password</label>
            <div className="workay-input-rel">
              <input
                type={showPassword ? 'text' : 'password'}
                className="workay-input pr-10"
                placeholder="Min 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                required
              />
              <button
                type="button"
                className="workay-eye-btn"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            {errors.password && <span className="workay-field-error">{errors.password}</span>}
          </div>
          <div className="workay-field">
            <label className="workay-label">Confirm new password</label>
            <div className="workay-input-rel">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                className="workay-input pr-10"
                placeholder="Re-enter password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
                required
              />
              <button
                type="button"
                className="workay-eye-btn"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={isLoading}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            {errors.confirmPassword && (
              <span className="workay-field-error">{errors.confirmPassword}</span>
            )}
          </div>
          <button type="submit" className="workay-submit-btn" disabled={isLoading}>
            {isLoading ? 'Updating...' : 'Reset password'}
          </button>
        </form>
      </>
    );
  }

  return (
    <div className="dm-auth-page">
      <section className="workay-hero-section">
        <div className="workay-form-pane">
          <div className="workay-form-content">{body}</div>
        </div>
      </section>
    </div>
  );
};

export default ResetPassword;
